import { Injectable } from '@angular/core';
import { ProgramItem } from '@app/app/page-modules/programs/models/program.model';
import { Page } from '@app/app/shared/models/page';
import { ObservableResult } from '@app/app/shared/store';
import { Observable, of } from 'rxjs';
import {
  EmployerMemberDataService,
  MemberResultsCourse,
  MemberResultsResponse,
  MemberSummaryResponse,
  OrgClientSummary,
  ProgramListItem,
} from './employer-member-data.service';
import { MOCK_MEMBERS, MOCK_PROGRAMS } from './employer-mock.data';
import { COURSES_BY_PROGRAM } from './grade-distribution-mock.data';

@Injectable()
export class MockEmployerMemberDataService implements EmployerMemberDataService {
  getPrograms(_masterOnly = false): Observable<ProgramListItem[]> {
    return of(
      MOCK_PROGRAMS.map((p) => ({
        id: p.id,
        name: p.name,
        isMaster: false,
        childProgramCount: 0,
        memberCount: (MOCK_MEMBERS[p.id] ?? []).length,
        status: p.status,
      })),
    );
  }

  getClientPrograms(_orgClientId: string): ObservableResult<Page<ProgramItem>> {
    return ObservableResult.ofSuccess<Page<ProgramItem>>({
      content: [],
      totalNumberOfElement: 0,
      pageSize: 0,
      currentPageNumber: 0,
      numberOfPages: 0,
      hasContent: false,
    });
  }

  getClientSummary(_clientId: string): ObservableResult<OrgClientSummary> {
    return ObservableResult.ofSuccess<OrgClientSummary>({
      id: _clientId,
      organizationUid: '',
      name: 'Mock Client',
      code: 'MOCK',
      industry: null,
      country: null,
      contactName: '',
      contactEmail: '',
      status: 'ACTIVE',
      permissions: {
        adminRole: false,
        fullAnalytics: true,
        viewOnly: false,
      },
    });
  }

  getMemberSummary(programId: string, memberUid: string): Observable<MemberSummaryResponse> {
    const member = (MOCK_MEMBERS[programId] ?? []).find((m) => m.id === memberUid);
    if (!member) {
      return of({
        progress: 0,
        averageGrade: null,
        coursesCompleted: 0,
        totalCourses: 0,
        averagePreAssessment: null,
        averagePostAssessment: null,
        knowledgeGain: null,
        enrollmentState: 'active',
        cohort: null,
        company: null,
      });
    }

    const totalCourses = member.courses.length;
    const coursesCompleted = member.courses.filter((c) => c.pct === 100).length;
    const averageGrade = Math.round(60 + member.progress * 0.25);
    const averagePreAssessment = Math.max(45, averageGrade - 22);
    const averagePostAssessment = Math.min(95, averageGrade + 2);

    return of({
      progress: member.progress,
      averageGrade,
      coursesCompleted,
      totalCourses,
      averagePreAssessment,
      averagePostAssessment,
      knowledgeGain: averagePostAssessment - averagePreAssessment,
      enrollmentState: member.status === 'at-risk' ? 'active' : member.status,
      cohort: member.cohort,
      company: 'Zain Group',
    });
  }

  getMemberResults(programId: string, memberUid: string): Observable<MemberResultsResponse> {
    const member = (MOCK_MEMBERS[programId] ?? []).find((m) => m.id === memberUid);
    const courseGrades = COURSES_BY_PROGRAM[programId] ?? [];

    if (!member) {
      return of({
        passRate: null,
        totalTrainingHours: null,
        yourGrade: null,
        preAssessment: null,
        postAssessment: null,
        gain: null,
        cohortAverage: null,
        courses: [],
      });
    }

    const completedCount = member.courses.filter((c) => c.pct === 100).length;
    const passRate = member.courses.length ? Math.round((completedCount / member.courses.length) * 100) : 0;
    const totalTrainingHours = courseGrades.reduce((s, c) => s + c.hoursAvg, 0);

    const yourGrade = Math.round(60 + member.progress * 0.25);
    const preAssessment = Math.max(45, yourGrade - 22);
    const postAssessment = Math.min(95, yourGrade + 2);
    const cohortAverage = courseGrades.length ? Math.round(courseGrades.reduce((s, c) => s + c.final, 0) / courseGrades.length) : yourGrade;

    const courses: MemberResultsCourse[] = member.courses.map((c, i) => {
      const cg = courseGrades[i];
      const isCompleted = c.pct === 100;
      return {
        courseId: cg?.id ?? String(i),
        courseName: c.name,
        status: isCompleted ? 'Ended' : 'Active',
        avgGrade: isCompleted && cg ? cg.final : null,
        hours: cg?.hoursAvg ?? 0,
        preAssessment: isCompleted && cg ? cg.pre : null,
        postAssessment: isCompleted && cg ? cg.post : null,
        gain: isCompleted && cg ? cg.post - cg.pre : null,
        logicalCourseName: cg?.short ?? null,
        cohort: member.cohort,
      };
    });

    return of({
      passRate,
      totalTrainingHours,
      yourGrade,
      preAssessment,
      postAssessment,
      gain: postAssessment - preAssessment,
      cohortAverage,
      courses,
    });
  }
}
